/**
 * QuetureTestPage - 공통 깨처 테스트 페이지 컴포넌트
 *
 * 개발/테스트 환경에서 Queture ID를 입력하여 깨처 영상을 재생하고,
 * PauseAndGo 트리거를 테스트하는 페이지.
 * apps/company, apps/lc 등에서 API만 주입하여 사용.
 */
import { useEffect, useRef, useState, useCallback } from 'react'
import Hls from 'hls.js'
import {
  Loader2,
  Search,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Film,
  BookImage,
  HelpCircle,
  Grid3X3,
  Check,
} from 'lucide-react'
import { useQuetureTest } from './useQuetureTest'
import { usePauseAndGo } from './usePauseAndGo'
import { PauseAndGoOverlay } from './PauseAndGoOverlay'
import { PauseAndGoList } from './PauseAndGoList'
import { ToonIntro } from '../toon/ToonIntro'
import { ToonOutro } from '../toon/ToonOutro'
import { QueBoardSession } from '../tools/QueBoard/QueBoardSession'
import { QUETURE_IDS_PRESET, QUEBOARD_QUETURE_GROUPS, createLabels, formatTime } from './constants'
import type { QuetureTestPageProps, QuetureTestQueBoardConfig } from './types'
import type { ToonCard, ToonQuizCard } from '../toon/types'

type TestTab = 'video' | 'toon-intro' | 'toon-outro' | 'queboard'

export function QuetureTestPage({ api, toonApi, t }: QuetureTestPageProps) {
  const labels = createLabels(t)
  const [showQuickSelect, setShowQuickSelect] = useState(true)
  const [quickSelectTab, setQuickSelectTab] = useState<'pag' | 'queboard'>('pag')
  const [activeTab, setActiveTab] = useState<TestTab>('video')
  const [toonIntroCards, setToonIntroCards] = useState<ToonCard[] | null>(null)
  const [toonOutroCards, setToonOutroCards] = useState<ToonQuizCard[] | null>(null)
  const [toonLoading, setToonLoading] = useState(false)
  const [toonError, setToonError] = useState<string | null>(null)
  const [showToonOverlay, setShowToonOverlay] = useState(false)

  // ── QueBoard state ────────────────────────────────────────────────────
  const [queboardConfigs, setQueboardConfigs] = useState<QuetureTestQueBoardConfig[] | null>(null)
  const [showQueBoard, setShowQueBoard] = useState(false)
  const [completedBoards, setCompletedBoards] = useState<Set<number>>(new Set())

  // ── Queture test hook ──────────────────────────────────────────────────
  const {
    quetureId,
    setQuetureId,
    quetureData,
    isLoading,
    error,
    currentBundleItem,
    setCurrentBundleItem,
    isSummaryMode,
    currentHlsUrl,
    pauseAndGos,
    includeUnpublishedPauseAndGo,
    setIncludeUnpublishedPauseAndGo,
    handleFetch,
    handleReset: handleQuetureReset,
  } = useQuetureTest(api)

  // ── Inline HLS player refs & state ─────────────────────────────────────
  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)
  const progressBarRef = useRef<HTMLDivElement>(null)

  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [progress, setProgress] = useState(0)

  // ── Video control callbacks (for usePauseAndGo) ───────────────────────
  const handlePause = useCallback(() => {
    videoRef.current?.pause()
  }, [])

  const handlePlay = useCallback(() => {
    videoRef.current?.play().catch(console.error)
  }, [])

  const handleSeek = useCallback((timeMs: number) => {
    const video = videoRef.current
    if (video) {
      video.currentTime = timeMs / 1000
    }
  }, [])

  // ── PauseAndGo hook ────────────────────────────────────────────────────
  const {
    currentPag,
    progress: pagProgress,
    handleVideoProgress: handlePagProgress,
    handleComplete: handlePagComplete,
    handleDismiss: handlePagDismiss,
    resetProgress: resetPagProgress,
    filteredPauseAndGos,
  } = usePauseAndGo({
    pauseAndGos,
    currentBundleItem,
    onPause: handlePause,
    onPlay: handlePlay,
    onSeek: handleSeek,
  })

  // ── Initialize HLS player when hlsUrl changes ─────────────────────────
  useEffect(() => {
    const video = videoRef.current
    if (!video || !currentHlsUrl) return

    // Reset player state
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)
    setProgress(0)

    if (hlsRef.current) {
      hlsRef.current.destroy()
      hlsRef.current = null
    }

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        xhrSetup: (xhr) => {
          xhr.withCredentials = false
        },
      })

      hls.loadSource(currentHlsUrl)
      hls.attachMedia(video)

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          console.error('HLS fatal error:', data.type, data.details)
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError()
          }
        }
      })

      hlsRef.current = hls
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = currentHlsUrl
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }
    }
  }, [currentHlsUrl])

  // ── Video event listeners ──────────────────────────────────────────────
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const onTimeUpdate = () => {
      const pct = (video.currentTime / video.duration) * 100 || 0
      setCurrentTime(video.currentTime)
      setProgress(pct)

      // Feed time (in ms) to PauseAndGo trigger detector
      handlePagProgress(video.currentTime * 1000)
    }

    const onDurationChange = () => setDuration(video.duration)
    const onPlay = () => setIsPlaying(true)
    const onPause = () => setIsPlaying(false)

    video.addEventListener('timeupdate', onTimeUpdate)
    video.addEventListener('durationchange', onDurationChange)
    video.addEventListener('play', onPlay)
    video.addEventListener('pause', onPause)

    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate)
      video.removeEventListener('durationchange', onDurationChange)
      video.removeEventListener('play', onPlay)
      video.removeEventListener('pause', onPause)
    }
  }, [handlePagProgress])

  // ── Player UI helpers ──────────────────────────────────────────────────
  const togglePlay = () => {
    const video = videoRef.current
    if (!video) return
    if (isPlaying) video.pause()
    else video.play().catch(console.error)
  }

  const toggleMute = () => {
    const video = videoRef.current
    if (!video) return
    video.muted = !video.muted
    setIsMuted(!isMuted)
  }

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current
    const bar = progressBarRef.current
    if (!video || !bar) return
    const rect = bar.getBoundingClientRect()
    const pos = (e.clientX - rect.left) / rect.width
    video.currentTime = pos * video.duration
  }

  const fmtSec = (sec: number) => {
    if (!sec || isNaN(sec)) return '00:00'
    const m = Math.floor(sec / 60)
    const s = Math.floor(sec % 60)
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }

  // ── Quick select handler ───────────────────────────────────────────────
  const handleQuickSelect = useCallback(
    (id: string) => {
      resetPagProgress()
      handleFetch(id, labels.fetchFailed)
    },
    [handleFetch, labels.fetchFailed, resetPagProgress]
  )

  const onFetch = useCallback(() => {
    resetPagProgress()
    handleFetch(undefined, labels.fetchFailed)
  }, [handleFetch, labels.fetchFailed, resetPagProgress])

  const handleReset = useCallback(() => {
    handleQuetureReset()
    resetPagProgress()
    setToonIntroCards(null)
    setToonOutroCards(null)
    setToonError(null)
    setQueboardConfigs(null)
    setCompletedBoards(new Set())
  }, [handleQuetureReset, resetPagProgress])

  // Toon 데이터 로딩
  const fetchToonData = useCallback(async (qid: string) => {
    if (!toonApi) return
    setToonLoading(true)
    setToonError(null)
    try {
      const data = await toonApi.getQuetureContent(qid)
      setToonIntroCards((data.qnIntro as ToonCard[]) ?? null)
      setToonOutroCards((data.qnOutro as ToonQuizCard[]) ?? null)
      const qbConfigs = data.queboardConfig ?? null
      setQueboardConfigs(qbConfigs)
      if (qbConfigs && qbConfigs.length > 0) setShowQueBoard(true)
    } catch {
      setToonError('깨봉툰 데이터를 불러올 수 없습니다.')
    } finally {
      setToonLoading(false)
    }
  }, [toonApi])

  // quetureData 로드 시 toon 데이터도 함께 로드
  useEffect(() => {
    if (quetureData?.quetureId && toonApi) {
      fetchToonData(quetureData.quetureId)
    }
  }, [quetureData?.quetureId, toonApi, fetchToonData])

  // ── Toggle includeUnpublished → refetch ────────────────────────────────
  const handleToggleUnpublished = useCallback(
    (checked: boolean) => {
      setIncludeUnpublishedPauseAndGo(checked)
      // 이미 로드된 데이터가 있으면 재조회
      if (quetureData) {
        resetPagProgress()
        // useEffect dependency로 자동 재조회가 되지 않으므로 명시적 refetch
        setTimeout(() => {
          handleFetch(undefined, labels.fetchFailed)
        }, 0)
      }
    },
    [setIncludeUnpublishedPauseAndGo, quetureData, resetPagProgress, handleFetch, labels.fetchFailed]
  )

  // ── Seek from PauseAndGo list click ────────────────────────────────────
  const handleSeekTo = useCallback((timeMs: number) => {
    const video = videoRef.current
    if (video) {
      video.currentTime = timeMs / 1000
    }
  }, [])

  return (
    <div className="p-6 mx-auto space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Queture Test Page</h1>
        {/* 탭 선택 */}
        {toonApi && quetureData && (
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {([
              { key: 'video' as TestTab, label: 'Video', icon: Film },
              { key: 'toon-intro' as TestTab, label: 'Toon Intro', icon: BookImage },
              { key: 'toon-outro' as TestTab, label: 'Toon Outro', icon: HelpCircle },
              { key: 'queboard' as TestTab, label: 'QueBoard', icon: Grid3X3 },
            ]).map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors ${
                  activeTab === key
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Search Form ───────────────────────────────────────────────── */}
      <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-2xs">
        <div className="space-y-4">
          {/* Quick Select */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setShowQuickSelect(!showQuickSelect)}
              className="flex gap-2 items-center text-sm font-medium text-gray-700 transition-colors hover:text-gray-900"
            >
              {showQuickSelect ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              <BookOpen className="w-4 h-4" />
              {labels.quickSelect}
            </button>

            {showQuickSelect && (
              <div className="pt-2 pb-4 border-b border-gray-200">
                {/* Tabs */}
                <div className="flex gap-1 mb-3 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => setQuickSelectTab('pag')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px
                      ${quickSelectTab === 'pag'
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                  >
                    PauseAndGo
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickSelectTab('queboard')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px
                      ${quickSelectTab === 'queboard'
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                  >
                    깨보드
                  </button>
                </div>

                {/* PauseAndGo 탭 */}
                {quickSelectTab === 'pag' && (
                  <div className="space-y-3">
                    {Object.entries(QUETURE_IDS_PRESET).map(([category, ids]) => (
                      <div key={category} className="space-y-2">
                        <label className="text-xs font-semibold text-gray-600 uppercase">{category}</label>
                        <div className="flex flex-wrap gap-2">
                          {ids.map((id) => (
                            <button
                              key={id}
                              onClick={() => handleQuickSelect(id)}
                              disabled={isLoading}
                              className={`px-3 py-1.5 text-xs font-mono rounded-md border transition-colors
                                ${
                                  quetureId === id
                                    ? 'bg-indigo-600 text-white border-indigo-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400 hover:bg-indigo-50'
                                }
                                ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {id}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* 깨보드 탭 */}
                {quickSelectTab === 'queboard' && (
                  <div className="space-y-3">
                    {Object.entries(QUEBOARD_QUETURE_GROUPS).map(([group, ids]) => (
                      <div key={group} className="space-y-2">
                        <label className="text-xs font-semibold text-gray-600">
                          {group}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {ids.map((id) => (
                            <button
                              key={id}
                              onClick={() => handleQuickSelect(id)}
                              disabled={isLoading}
                              className={`px-3 py-1.5 text-xs font-mono rounded-md border transition-colors
                                ${
                                  quetureId === id
                                    ? 'bg-emerald-600 text-white border-emerald-600'
                                    : 'bg-white text-emerald-800 border-emerald-300 hover:border-emerald-400 hover:bg-emerald-50'
                                }
                                ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {id}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Queture ID Input */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label htmlFor="quetureId" className="sr-only">
                {labels.quetureIdLabel}
              </label>
              <input
                id="quetureId"
                type="text"
                placeholder={labels.quetureIdPlaceholder}
                value={quetureId}
                onChange={(e) => setQuetureId(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onFetch()}
                className="px-4 py-2 w-full text-lg rounded-lg border border-gray-300 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={onFetch}
              disabled={isLoading}
              className="flex gap-2 items-center px-6 py-2 font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {labels.search}
            </button>
          </div>

          {/* includeUnpublishedPauseAndGo Toggle */}
          <label className="flex gap-2 items-center cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeUnpublishedPauseAndGo}
              onChange={(e) => handleToggleUnpublished(e.target.checked)}
              className="w-4 h-4 text-indigo-600 rounded-xs border-gray-300 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">{labels.includeUnpublished}</span>
          </label>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Loading ───────────────────────────────────────────────────── */}
      {isLoading && (
        <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-2xs">
          <div className="flex justify-center items-center py-12">
            <div className="flex flex-col gap-4 items-center">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
              <p className="text-gray-600">{labels.loading}</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Queture Display ───────────────────────────────────────────── */}
      {quetureData && activeTab === 'video' && (
        <>
          {/* Header + Controls */}
          <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-2xs space-y-4">
            {/* Queture Info */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <div className="space-y-1">
                <p className="text-sm text-gray-500">
                  Queture ID: <span className="font-mono">{quetureData.quetureId}</span>
                </p>
                {quetureData.title && (
                  <p className="text-base font-medium text-gray-900">{quetureData.title}</p>
                )}
                <p className="text-xs text-gray-400">
                  PauseAndGo: {pauseAndGos.length}개
                  {quetureData.hasSummary && ' · Summary 있음'}
                </p>
              </div>
              <div className="flex gap-2 items-center">
                {/* Summary Toggle */}
                {quetureData.hasSummary && (
                  <label className="flex gap-2 items-center px-3 py-1.5 text-sm rounded-lg border border-amber-300 bg-amber-50 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={isSummaryMode}
                      onChange={(e) => {
                        setCurrentBundleItem(e.target.checked ? 'summary' : 'main')
                        resetPagProgress()
                      }}
                      className="w-4 h-4 text-amber-600 rounded-xs border-gray-300 focus:ring-amber-500"
                    />
                    <span className="text-amber-800 font-medium">{labels.summaryToggle}</span>
                  </label>
                )}
                <button
                  onClick={handleReset}
                  className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  {labels.reset}
                </button>
              </div>
            </div>

            {/* ── Inline Video Player ──────────────────────────────────── */}
            <div className="relative bg-black rounded-xl overflow-hidden aspect-video">
              <video
                ref={videoRef}
                className="w-full h-full"
                playsInline
                onClick={togglePlay}
              />

              {/* PauseAndGo Overlay */}
              {currentPag && (
                <PauseAndGoOverlay
                  pag={currentPag}
                  onComplete={handlePagComplete}
                  onDismiss={handlePagDismiss}
                  labels={labels}
                  quizApi={api.quizApi}
                />
              )}

              {/* Play button overlay (when paused and no PAG) */}
              {!isPlaying && !currentPag && currentHlsUrl && (
                <div
                  className="flex absolute inset-0 justify-center items-center cursor-pointer bg-black/30"
                  onClick={togglePlay}
                >
                  <div className="flex justify-center items-center w-16 h-16 rounded-full bg-white/20 backdrop-blur-xs">
                    <Play className="ml-1 w-8 h-8 text-white" />
                  </div>
                </div>
              )}

              {/* Controls bar */}
              <div className="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/80 to-transparent p-3 space-y-2">
                {/* Progress bar with PAG markers */}
                <div
                  ref={progressBarRef}
                  className="relative cursor-pointer group/progress"
                  onClick={handleProgressClick}
                >
                  <div className="w-full h-2 rounded-full bg-white/20">
                    <div
                      className="h-full bg-indigo-500 rounded-full"
                      style={{ width: `${Math.max(progress, 0)}%` }}
                    />
                  </div>

                  {/* PAG markers on progress bar */}
                  {duration > 0 &&
                    filteredPauseAndGos.map((pag) => {
                      const pct = (pag.time / 1000 / duration) * 100
                      const isCompleted = pagProgress[pag.id]?.completed
                      return (
                        <div
                          key={pag.id}
                          className={`absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full border border-white/80 ${
                            isCompleted ? 'bg-green-400' : 'bg-yellow-400'
                          }`}
                          style={{ left: `${Math.min(Math.max(pct, 0), 100)}%`, transform: 'translate(-50%, -50%)' }}
                          title={`${formatTime(pag.time)} — ${pag.type}`}
                        />
                      )
                    })}
                </div>

                {/* Buttons */}
                <div className="flex justify-between items-center">
                  <div className="flex gap-2 items-center">
                    <button onClick={togglePlay} className="p-1.5 text-white rounded-xs bg-black/40 hover:bg-black/60">
                      {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                    </button>
                    <button onClick={toggleMute} className="p-1.5 text-white rounded-xs bg-black/40 hover:bg-black/60">
                      {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    </button>
                    <span className="px-2 py-0.5 text-xs font-mono text-white bg-black/40 rounded-xs">
                      {fmtSec(currentTime)} / {fmtSec(duration)}
                    </span>
                    {/* Bundle indicator */}
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                      isSummaryMode ? 'bg-amber-500 text-white' : 'bg-indigo-500 text-white'
                    }`}>
                      {isSummaryMode ? 'summary' : 'main'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── PauseAndGo List ──────────────────────────────────────── */}
          <PauseAndGoList
            pauseAndGos={pauseAndGos}
            progress={pagProgress}
            currentBundleItem={currentBundleItem}
            onSeekTo={handleSeekTo}
            labels={labels}
            videoDuration={duration}
          />
        </>
      )}
      {/* ── Toon Intro Tab ──────────────────────────────────────────── */}
      {quetureData && activeTab === 'toon-intro' && (
        <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-2xs space-y-4">
          <div className="flex justify-between items-center pb-4 border-b border-gray-200">
            <div className="space-y-1">
              <p className="text-sm text-gray-500">
                Queture ID: <span className="font-mono">{quetureData.quetureId}</span>
              </p>
              <p className="text-base font-medium text-gray-900">깨봉툰 인트로 (qn_i)</p>
            </div>
          </div>

          {toonLoading && (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
          )}

          {toonError && (
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-700">{toonError}</p>
            </div>
          )}

          {!toonLoading && !toonError && toonIntroCards && toonIntroCards.length > 0 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">{toonIntroCards.length}개 카드</p>
              <button
                onClick={() => setShowToonOverlay(true)}
                className="px-4 py-2 font-medium text-white bg-orange-400 rounded-lg hover:bg-orange-500 transition-colors"
              >
                인트로 시작
              </button>
              {/* 카드 미리보기 */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {toonIntroCards.map((card, i) => (
                  <div key={i} className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
                    <span className="text-xs font-mono text-gray-400">#{i + 1}</span>
                    {card.imageUrl && (
                      <div
                        className="w-full h-24 bg-center bg-contain bg-no-repeat rounded-xs"
                        style={{ backgroundImage: `url(${card.imageUrl})` }}
                      />
                    )}
                    {card.text && <p className="text-xs text-gray-700 line-clamp-2">{card.text}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!toonLoading && !toonError && (!toonIntroCards || toonIntroCards.length === 0) && (
            <p className="text-sm text-gray-500 py-8 text-center">이 깨처에 인트로 데이터가 없습니다.</p>
          )}
        </div>
      )}

      {/* ── Toon Outro Tab ──────────────────────────────────────────── */}
      {quetureData && activeTab === 'toon-outro' && (
        <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-2xs space-y-4">
          <div className="flex justify-between items-center pb-4 border-b border-gray-200">
            <div className="space-y-1">
              <p className="text-sm text-gray-500">
                Queture ID: <span className="font-mono">{quetureData.quetureId}</span>
              </p>
              <p className="text-base font-medium text-gray-900">깨봉툰 아웃트로 퀴즈 (qn_o)</p>
            </div>
          </div>

          {toonLoading && (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
          )}

          {toonError && (
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-700">{toonError}</p>
            </div>
          )}

          {!toonLoading && !toonError && toonOutroCards && toonOutroCards.length > 0 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">{toonOutroCards.length}개 퀴즈 카드</p>
              <button
                onClick={() => setShowToonOverlay(true)}
                className="px-4 py-2 font-medium text-white bg-orange-400 rounded-lg hover:bg-orange-500 transition-colors"
              >
                아웃트로 시작
              </button>
              {/* 카드 미리보기 */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {toonOutroCards.map((card, i) => (
                  <div key={i} className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
                    <span className="text-xs font-mono text-gray-400">#{i + 1}</span>
                    {card.imageUrl && (
                      <div
                        className="w-full h-24 bg-center bg-contain bg-no-repeat rounded-xs"
                        style={{ backgroundImage: `url(${card.imageUrl})` }}
                      />
                    )}
                    {card.text && <p className="text-xs text-gray-700 line-clamp-2">{card.text}</p>}
                    {card.answer0 && card.answer1 && (
                      <div className="flex gap-1 text-xs">
                        <span className={`px-1.5 py-0.5 rounded-xs ${card.correctAnswer === 'answer_0' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                          {card.answer0}
                        </span>
                        <span className={`px-1.5 py-0.5 rounded-xs ${card.correctAnswer === 'answer_1' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                          {card.answer1}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!toonLoading && !toonError && (!toonOutroCards || toonOutroCards.length === 0) && (
            <p className="text-sm text-gray-500 py-8 text-center">이 깨처에 아웃트로 데이터가 없습니다.</p>
          )}
        </div>
      )}

      {/* ── QueBoard Tab ───────────────────────────────────────────── */}
      {quetureData && activeTab === 'queboard' && (
        <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-2xs space-y-4">
          <div className="flex justify-between items-center pb-4 border-b border-gray-200">
            <div className="space-y-1">
              <p className="text-sm text-gray-500">
                Queture ID: <span className="font-mono">{quetureData.quetureId}</span>
              </p>
              <p className="text-base font-medium text-gray-900">깨보드 설정</p>
            </div>
          </div>

          {toonLoading && (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
          )}

          {toonError && (
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-700">{toonError}</p>
            </div>
          )}

          {!toonLoading && !toonError && queboardConfigs && queboardConfigs.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">{queboardConfigs.length}개 수식</p>
                <button
                  onClick={() => setShowQueBoard(true)}
                  className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  열기
                </button>
              </div>

              {/* 수식 카드 목록 (compact) */}
              <div className="flex flex-wrap gap-2">
                {queboardConfigs.map((cfg, i) => {
                  const isDone = completedBoards.has(i)
                  return (
                    <div
                      key={i}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                        isDone
                          ? 'border-green-300 bg-green-50'
                          : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className={`flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                        isDone
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-300 text-white'
                      }`}>
                        {isDone ? <Check className="w-3 h-3" /> : i + 1}
                      </div>
                      <span className="font-medium text-gray-900">
                        {cfg.problem.left} + {cfg.problem.right} = {cfg.problem.left + cfg.problem.right}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {!toonLoading && !toonError && (!queboardConfigs || queboardConfigs.length === 0) && (
            <p className="text-sm text-gray-500 py-8 text-center">이 깨처에 깨보드 설정이 없습니다.</p>
          )}
        </div>
      )}

      {/* ── QueBoard Session (네비게이션 포함) ────────────────────── */}
      {showQueBoard && queboardConfigs && queboardConfigs.length > 0 && (
        <QueBoardSession
          isOpen={showQueBoard}
          configs={queboardConfigs}
          onSessionComplete={(results) => {
            setCompletedBoards(new Set(results.map((_, i) => i).filter((i) => results[i].completed)))
          }}
        />
      )}

      {/* ── Toon Overlay (전체화면) ──────────────────────────────────── */}
      {showToonOverlay && activeTab === 'toon-intro' && toonIntroCards && (
        <ToonIntro
          cards={toonIntroCards}
          onComplete={() => setShowToonOverlay(false)}
          autoPlayAudio
        />
      )}
      {showToonOverlay && activeTab === 'toon-outro' && toonOutroCards && (
        <ToonOutro
          cards={toonOutroCards}
          onComplete={() => setShowToonOverlay(false)}
          autoPlayAudio
        />
      )}
    </div>
  )
}

export default QuetureTestPage
